import { Flex, Text } from '@mantine/core';
import pluralize from 'pluralize';
import { memo } from 'react';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { camelCaseOrSnakeCaseToWords } from '../merge-review/ReviewHelperFunctions';
import {
  DetectionState,
  EntityCategory,
  getLabelId,
  selectChanges,
  selectDetections,
  selectIsDiscarded,
  selectIsResolved,
  selectLastAction,
  selectNewFields,
  selectSelectedConflictIndex,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import { FieldTypeMetadata, FieldValue } from '../types';
import ConflictOption from './ConflictOption';
import { DataInput } from './DataInput';
import { DetectionOwnersEditor } from './DetectionOwnersEditor';
import EntityCell from './EntityCell';
import ValueChange from './ValueChange';

type GroupedEntityDataProps = {
  entityType: string;
  category: EntityCategory;
};

function GroupedEntityData({ entityType, category }: GroupedEntityDataProps) {
  const detections = useDetectedEntitiesStore(state =>
    selectDetections(state, category, entityType),
  );

  return (
    <Flex direction="column" gap="xs">
      {detections.map((detection, index) => {
        const entityName = pluralize.singular(camelCaseOrSnakeCaseToWords(detection.entityType));
        return (
          <EntityCell
            key={detection.id || index}
            grouped
            title={`${entityName} ${index + 1}`}
            NewComponent={<NewGroupedData category={category} detection={detection} />}
            ExistingComponent={<ExistingGroupedData category={category} detection={detection} />}
            category={category}
            detectionId={detection.id}
          />
        );
      })}
    </Flex>
  );
}

type GroupedDataProps = {
  category: EntityCategory;
  detection: DetectionState;
};

function NewGroupedData({ category, detection }: GroupedDataProps) {
  const newFields = useDetectedEntitiesStore(state =>
    selectNewFields(state, category, detection.id),
  );
  const { showDataUploadCounts } = useFlags();
  const lastAction = useDetectedEntitiesStore(state =>
    selectLastAction(state, category, detection.id),
  );
  const didInsert = lastAction?.type === 'insert';
  const showTitle = !didInsert && detection.conflicts.length > 0;

  return (
    <Flex direction="column" gap="xl" flex={1} miw={0}>
      {showTitle && (
        <Text fw={700} size="sm" c="gray.10">
          {showDataUploadCounts
            ? `${newFields.length} ${pluralize('item', newFields.length)}`
            : '\u00A0'}
        </Text>
      )}
      <Flex direction="column" gap="md">
        {newFields.map(({ key, value, metadata }) => {
          return (
            <GroupedDataField
              key={key}
              name={key}
              value={value}
              category={category}
              metadata={metadata}
              detection={detection}
            />
          );
        })}
      </Flex>
      <DetectionOwnersEditor category={category} detection={detection} />
    </Flex>
  );
}

type Props = {
  name: string;
  value: FieldValue;
  metadata: FieldTypeMetadata;
  detection: DetectionState;
  category: EntityCategory;
};

function GroupedDataField({ name, value, metadata, detection, category }: Props) {
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detection.id, name),
  );
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detection.id, name),
  );
  const isReadOnly = typeof value === 'object' && value !== null;
  const isDisabled = isResolved || isDiscarded;
  const labelId = getLabelId(category, detection.id, name);

  return (
    <Flex gap="xxxs" align="center" data-testid="new-grouped-field">
      <Text
        fw={500}
        size="sm"
        c="gray.10"
        opacity={isDisabled ? 0.5 : 1}
        style={{
          wordBreak: 'keep-all',
        }}
        data-testid="grouped-field-label"
        id={labelId}
      >
        {metadata.label ?? camelCaseOrSnakeCaseToWords(name)}:
      </Text>
      <DataInput
        category={category}
        editable={!isResolved && !isReadOnly}
        highlight="new"
        metadata={metadata}
        detectionId={detection.id}
        fieldName={name}
        allowDiscard
        data-testid="grouped-field-input"
        ariaLabelledBy={labelId}
      >
        {value}
      </DataInput>
    </Flex>
  );
}

function ExistingGroupedData({ category, detection }: GroupedDataProps) {
  const conflicts = detection.conflicts;
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detection.id),
  );
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detection.id),
  );
  const changes = useDetectedEntitiesStore(state => selectChanges(state, category, detection.id));
  const selectedConflictIndex = useDetectedEntitiesStore(state =>
    selectSelectedConflictIndex(state, category, detection.id),
  );
  const entityName = pluralize.singular(detection.entityLabel.toLowerCase());
  const lastAction = useDetectedEntitiesStore(state =>
    selectLastAction(state, category, detection.id),
  );
  const didInsert = lastAction?.type === 'insert';
  const showTitle = !didInsert && detection.conflicts.length > 0;

  if (isDiscarded) {
    return null;
  }

  if (didInsert) {
    return (
      <Flex direction="column" gap="xl" data-testid="resolved-changes">
        {showTitle && (
          <Text fw={700} size="sm" c="gray.10">
            &nbsp;
          </Text>
        )}
        <Flex direction="column" gap="md">
          {changes.map(change => {
            return (
              <ValueChange
                key={change.key}
                category={category}
                label={change.metadata.label ?? camelCaseOrSnakeCaseToWords(change.key)}
                newValue={change.value}
                previousValue={change.previousValue}
                metadata={change.metadata}
              />
            );
          })}
        </Flex>
      </Flex>
    );
  }

  if (isResolved) {
    return (
      <Flex direction="column" gap="xl" data-testid="resolved-changes">
        <Text fw={700} size="sm" c="gray.10">
          Existing {entityName} {(selectedConflictIndex ?? 0) + 1}
        </Text>
        <Flex direction="column" gap="md">
          {changes.map(change => {
            return (
              <ValueChange
                key={change.key}
                category={category}
                label={change.metadata.label ?? camelCaseOrSnakeCaseToWords(change.key)}
                newValue={change.value}
                previousValue={change.previousValue}
              />
            );
          })}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="xl" data-testid="conflicts">
      {conflicts.length > 1 && (
        <Text fw={700} size="sm" c="gray.10">
          Select to update
        </Text>
      )}
      <Flex direction="column" gap="md">
        {conflicts.map((conflict, index) => {
          return (
            <ConflictOption key={index} category={category} detection={detection} index={index} />
          );
        })}
      </Flex>
    </Flex>
  );
}

export default memo(GroupedEntityData);
